import { isNil } from "@clipboard-health/util-ts";
import { getCognitoIdToken, hasStoredCognitoSession } from "@src/appV2/Auth/cognito/cognitoAuth";
import { redirectToLogin } from "@src/appV2/Auth/redirectToLogin";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import axios from "axios";

import { apiClient } from "./apiClient";

export async function handleApiResponseError(error: unknown): Promise<unknown> {
  if (
    !axios.isAxiosError(error) ||
    error.response?.status !== 401 ||
    error.config?.skipAuthRedirectOnUnauthorized
  ) {
    if (error instanceof Error) {
      throw error;
    }
    return;
  }

  const { config } = error;

  if (isNil(config) || !hasStoredCognitoSession()) {
    redirectToLogin("apiClient");
    return new Promise<never>(() => {});
  }

  if (config.authRetried) {
    logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
      from: globalThis.location?.pathname,
      reason: "unauthorized_after_force_refresh",
    });
    throw error;
  }

  logEvent(APP_V2_APP_EVENTS.API_401_RETRY_WITH_FORCE_REFRESH, {
    from: globalThis.location?.pathname,
  });

  let forceRefreshSucceeded = false;
  try {
    const refreshedToken = await getCognitoIdToken(true);
    forceRefreshSucceeded = Boolean(refreshedToken);
  } catch {
    forceRefreshSucceeded = false;
  }

  if (!forceRefreshSucceeded) {
    logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
      from: globalThis.location?.pathname,
      reason: "no_token_after_force_refresh",
    });
    redirectToLogin("apiClient");
    return new Promise<never>(() => {});
  }

  // Drop the stale header so the request interceptor re-fetches and the
  // `Authorization`-present branch doesn't flip `skipAuthRedirectOnUnauthorized`.
  config.authRetried = true;
  config.headers?.delete?.("Authorization");

  try {
    const retryResponse = await apiClient.request(config);
    logEvent(APP_V2_APP_EVENTS.API_401_RETRY_SUCCEEDED, {
      from: globalThis.location?.pathname,
    });
    return retryResponse;
  } catch (retryError: unknown) {
    if (!isUnauthorizedAfterForceRefreshError(retryError)) {
      logEvent(APP_V2_APP_EVENTS.API_401_RETRY_FAILED, {
        from: globalThis.location?.pathname,
        reason: "retry_request_failed",
      });
    }
    throw retryError;
  }
}

function isUnauthorizedAfterForceRefreshError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response?.status === 401 &&
    error.config?.authRetried === true
  );
}
